<template>
  <create-account-layout
    :current-step="3"
    title="Your Profile"
  >
    <form
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div>
        <p class="my-6 text-left text-dark-gray">
          Let's get some basic information.
        </p>
        <div>
          <div class="flex flex-col gap-2 text-left">
            <label
              class="w-full text-left text-2xs font-bold uppercase text-dark-blue"
            >
              Verified full name
            </label>
            <p class="mb-1 text-body-color">
              {{ fullName }}
            </p>
          </div>
          <form-field
            id="preferred_name"
            label="Preferred name (Required)"
            name="preferred_name"
            placeholder="Enter your preferred name"
            :value="preferredName"
            :error="full_name_error"
            :alert-message="full_name_alert"
            @input="handlePreferredName"
          />
          <form-field
            v-if="isDispatcherServiceSelected"
            id="dispatch_service_company_name"
            label="Enter your Dispatch Company Name"
            name="dispatch_service_company_name"
            placeholder="Enter your preferred name"
            :value="dispatchServiceCompanyName"
            @input="handleDispatchServiceCompanyName"
          />
        </div>
        <button-main
          class="mb-4 mt-3"
          :disabled="isButtonDisable"
          type="submit"
          :loading-spinner="isLoading"
          value="Continue"
        />
      </div>
    </form>
  </create-account-layout>
</template>
<script setup>
//We are treating the first name as the preferred name. Once Persona returns the results, we immediately store the first and last name in the database,
//setting the first name as the default preferred name

import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { ref, computed, onMounted } from 'vue';
import { useIdentityVerification } from '@/composables/useIdentityVerification.js';
import CreateAccountLayout from '@/components/layouts/CreateAccountLayout.vue';
import ButtonMain from '@/components/Button.vue';
import FormField from '@/components/FormField.vue';
import { toastResponse } from '@/common/handleServerResponse';

const store = useStore();
const router = useRouter();
const preferredName = ref('');
const dispatchServiceCompanyName = ref('');
const full_name_error = ref(null);
const full_name_alert = ref(null);
const isLoading = ref(false);
const { redirectBasedOnCompanyType } = useIdentityVerification();

const currentUser = computed(() => store.state.users.currentUser);
const fullName = computed(
  () => `${currentUser.value?.first_name} ${currentUser.value?.last_name}`
);

const storedPreferedName = computed(
  () => store.state.users.currentUser?.preferred_name
);

const isVerifiedEmail = computed(
  () => store.state.users.currentUser?.is_verified_email
);
const companyType = computed(
  () => store.state.users.currentUser?.company?.company_type
);

const isDispatcherServiceSelected = computed(
  () => store.state.signUpData?.company_type === 'dispatch_service'
);

const isButtonDisable = computed(() => {
  if (isDispatcherServiceSelected.value) {
    return (
      preferredName.value.length === 0 ||
      dispatchServiceCompanyName.value.length === 0
    );
  }
  return preferredName?.value?.length === 0;
});

const handleSubmit = async () => {
  try {
    isLoading.value = true;

    const res = await store.dispatch(
      'users/updatePreferredName',
      preferredName.value
    );

    if (res.status === 200 || res.status === 201) {
      handleRedirect();
      return;
    } else {
      console.warn('Unexpected response status:', res.status);
    }

    console.log(res);
  } catch (error) {
    console.error('Error in handleSubmit:', error);
    toastResponse(error?.message || 'An error occurred');
  } finally {
    isLoading.value = false;
  }
};

const handleDispatchServiceCompanyName = (event) => {
  dispatchServiceCompanyName.value = event.target.value;
  store.commit('dispatchers/setDispatchServiceCompanyName', event.target.value);
};

const redirectAfterIDV = computed(
  () => store.getters['identityVerification/getRedirectAfterIDV']
);

const isEmpty = (value) => value == null || value === '';

const handleRedirect = () => {
  console.log('Redirecting to:', redirectAfterIDV?.value);
  // const redirectRoute = isVerifiedEmail.value
  //   ? '/sign-up/fmsca-account-verification'
  //   : '/sign-up/company-email';
  if (companyType.value && isVerifiedEmail.value) {
    return redirectBasedOnCompanyType(companyType.value);
  }

  if (!isVerifiedEmail.value) {
    router.push('/sign-up/company-email');
  } else {
    console.log('Redirecting to:', redirectAfterIDV?.value);
    if (
      isEmpty(redirectAfterIDV.value?.name) &&
      isEmpty(redirectAfterIDV.value?.path)
    ) {
      console.log('Validating code');
      validateLogic();
    }
    redirectAfterIDV.value?.name?.length > 0
      ? router.push({ name: redirectAfterIDV.value.name })
      : router.push(redirectAfterIDV.value.path);
  }
};

const isChallengeRequired = computed(
  () => store.state.verifications?.challenge_question_required || false
);

const isEmailRequiredForLogin = computed(
  () => store.state.carriers?.isEmailRequiredForLogin || false
);

const handleBigCarrierSubmit = async () => {
  const res = await store.dispatch('users/hasCarrierBeenClaimed');
  if (res.status === 200) {
    store.commit('carriers/setMaskedContacts', res.data);
  }
  if (isChallengeRequired.value === true) {
    router.push({
      name: 'VerificationChallenge',
    });
  } else if (res.status === 200) {
    router.push({
      name: 'RequestAccessFMSCA',
      params: { carrierHasBeenClaimed: true },
    });
  } else if (res.status === 404) {
    if (isChallengeRequired.value === true) {
      router.push({
        name: 'VerificationChallenge',
      });
    } else {
      router.push({
        name: 'FMSCAAccountVerification',
        params: { canMoveOn: true },
      });
    }
  } else {
    toastResponse(res);
  }
};

const validateLogic = async () => {
  isLoading.value = true;
  const res = await store.dispatch('users/hasCarrierBeenClaimed');

  if (isEmailRequiredForLogin.value) {
    await handleBigCarrierSubmit();
    return;
  }
  if (isChallengeRequired.value === true) {
    router.push({
      name: 'VerificationChallenge',
    });
  } else {
    if (res.status === 200) {
      store.commit('carriers/setMaskedContacts', res.data);
    }
    if (res.status === 200) {
      router.push({
        name: 'RequestAccessFMSCA',
        params: { carrierHasBeenClaimed: true },
      });
    } else if (res.status === 404) {
      router.push({
        name: 'FMSCAAccountVerification',
        params: { canMoveOn: true },
      });
    } else {
      toastResponse(res);
    }
  }
  isLoading.value = false;
};

const handlePreferredName = (event) => {
  preferredName.value = event.target.value;
};

onMounted(async () => {
  await store.dispatch('users/serverRefreshCurrentUser');

  preferredName.value = storedPreferedName.value;
  const findInput = document.querySelector('#preferred_name');
  setTimeout(() => {
    findInput?.focus();
  }, 0);
});
</script>

<style scoped>
p {
  @apply text-sm !important;
}
</style>
